const recommendationsRequestCache = new Map();

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.revealCleanupTimer = null;
  }

  static hasRenderableProducts(recommendationsNode) {
    if (!recommendationsNode?.innerHTML?.trim().length) return false;
    return recommendationsNode.querySelector('[data-product-card], swiper-slide, .card-product') !== null;
  }

  /**
   * When SSR already showed cards (no recommendations-pending), keep the section visible.
   * Avoid stripping recommendations-ready because CSS can hide related-products without it.
   */
  static showSection(sectionWrapper) {
    if (!sectionWrapper) return;

    sectionWrapper.classList.remove('d-none');

    if (!sectionWrapper.classList.contains('recommendations-pending')) {
      sectionWrapper.classList.add('recommendations-shell');
      sectionWrapper.classList.add('recommendations-ready');
      sectionWrapper.classList.remove('recommendations-pending');
      return;
    }

    sectionWrapper.classList.remove('recommendations-ready');
    sectionWrapper.classList.add('recommendations-shell');
    requestAnimationFrame(() => {
      sectionWrapper.classList.remove('recommendations-pending');
      sectionWrapper.classList.add('recommendations-ready');
    });
  }

  static hideSection(sectionWrapper) {
    if (!sectionWrapper) return;
    sectionWrapper.classList.remove('recommendations-ready', 'recommendations-pending', 'recommendations-shell');
    sectionWrapper.classList.add('d-none');
  }

  async fetchRecommendationsHtml(url) {
    let requestPromise = recommendationsRequestCache.get(url);
    if (!requestPromise) {
      requestPromise = Utility.Fetch.html(url).catch((error) => {
        recommendationsRequestCache.delete(url);
        throw error;
      });
      recommendationsRequestCache.set(url, requestPromise);
    }
    return requestPromise;
  }

  async connectedCallback() {
    if (this.dataset.loaded === 'true' || this.isLoading) return;

    const url = this.dataset.url;
    if (!url) return;

    const sectionWrapper = this.closest('[data-recommendations-section]');
    // Skip refetch when Liquid already rendered cards.
    if (
      sectionWrapper?.classList.contains('recommendations-ready') &&
      ProductRecommendations.hasRenderableProducts(this)
    ) {
      this.dataset.loaded = 'true';
      return;
    }

    this.isLoading = true;
    const hadServerRenderedProducts = sectionWrapper?.classList.contains('recommendations-ready');
    const hadProducts = hadServerRenderedProducts || this.querySelector('[data-product-card], swiper-slide, .card-product') !== null;

    if (sectionWrapper) {
      if (!hadProducts) {
        sectionWrapper.classList.remove('d-none', 'recommendations-ready');
        sectionWrapper.classList.add('recommendations-shell', 'recommendations-pending');
      } else {
        sectionWrapper.classList.remove('d-none');
      }
    }

    try {
      let text = await this.fetchRecommendationsHtml(url);
      let recommendations = Utility.DOM.extract(text, 'product-recommendations');
      let hasProducts = ProductRecommendations.hasRenderableProducts(recommendations);

      if (!hasProducts) {
        recommendationsRequestCache.delete(url);
        await new Promise((resolve) => setTimeout(resolve, 550));
        text = await this.fetchRecommendationsHtml(url);
        recommendations = Utility.DOM.extract(text, 'product-recommendations');
        hasProducts = ProductRecommendations.hasRenderableProducts(recommendations);
      }

      if (recommendations && recommendations.innerHTML.trim().length) {
        if (hasProducts) {
          this.innerHTML = recommendations.innerHTML;
          this.dataset.loaded = 'true';
          if (url.includes('intent=complementary')) {
            const complementaryParent = Utility.DomSelector.$('[data-complementaryitems]');
            complementaryParent?.classList.remove('d-none');
          }

          ProductRecommendations.showSection(sectionWrapper);
          if (this.revealCleanupTimer) clearTimeout(this.revealCleanupTimer);
          this.revealCleanupTimer = setTimeout(() => {
            sectionWrapper?.classList.remove('recommendations-ready', 'recommendations-shell');
            this.revealCleanupTimer = null;
          }, 320);

          if (typeof lazyImageObserver !== 'undefined' && lazyImageObserver?.observe) {
            lazyImageObserver.observe();
          }
        } else {
          if (hadProducts) {
            this.dataset.loaded = 'true';
            ProductRecommendations.showSection(sectionWrapper);
          } else {
            ProductRecommendations.hideSection(sectionWrapper);
          }
        }
      } else {
        if (hadProducts) {
          this.dataset.loaded = 'true';
          ProductRecommendations.showSection(sectionWrapper);
        } else {
          ProductRecommendations.hideSection(sectionWrapper);
        }
      }
    } catch (error) {
      if (hadProducts) {
        this.dataset.loaded = 'true';
        ProductRecommendations.showSection(sectionWrapper);
      } else {
        ProductRecommendations.hideSection(sectionWrapper);
      }
      console.error('Error fetching product recommendations:', error);
    } finally {
      this.isLoading = false;
    }
  }

  disconnectedCallback() {
    if (this.revealCleanupTimer) {
      clearTimeout(this.revealCleanupTimer);
      this.revealCleanupTimer = null;
    }
    this.isLoading = false;
  }
}

customElements.define('product-recommendations', ProductRecommendations);
